import { DRAW_MUST_MOVE, DRAW_LIMIT_REACHED, DRAW_TOO_FAST } from '@draughts/shared';
import { codiceErrore } from './codice-errore';

/**
 * Dal codice dell'ack di `game:offer-draw` alla chiave i18n.
 *
 * Una mappa per tutte e tre le schermate, e non una per dominio come per
 * l'arena e i tornei: il motivo di un rifiuto di patta non ha niente di
 * specifico del dominio — e' la stessa frase in partita, in torneo e in arena,
 * e vive in `game.actions` perche' e' il BOTTONE a mostrarla. Triplicarla
 * vorrebbe dire sei chiavi identiche per otto lingue.
 */
export const CHIAVI_RIFIUTO_PATTA: Readonly<Record<string, string>> = {
  [DRAW_MUST_MOVE]: 'game.actions.drawMustMoveFirst',
  [DRAW_LIMIT_REACHED]: 'game.actions.drawLimitReached',
  [DRAW_TOO_FAST]: 'game.actions.drawTooFast',
};

/**
 * La chiave da mostrare quando una proposta di patta viene rifiutata.
 *
 * Tre gradini, in ordine di precisione:
 *
 *  1. i codici del FRENO, qui sopra — la frase esatta, condivisa;
 *  2. `mappaDominio`, cioe' la mappa che lo store usa gia' (`CHIAVI_ERRORE_ONLINE`,
 *     `ARENA_ERROR_KEYS`, `TOURNAMENT_ERROR_KEYS`): e' li' che vivono le frasi
 *     precise per `AUTH_REQUIRED`, `ROOM_NOT_FOUND` e compagnia, e non vanno
 *     buttate via;
 *  3. `ripiego`, la chiave di dominio della patta (`online.errors.drawOfferFailed`
 *     e i suoi gemelli). **Non** la frase generica: «qualcosa non ha funzionato»
 *     direbbe MENO di quel che sapevamo già, cioè che era la patta a non essere
 *     partita.
 *
 * Il codice si legge con `codiceErrore`, che guarda `.code`: l'ack del server
 * porta la frase inglese in `.error`, e quella non deve mai arrivare a schermo.
 */
export function chiaveRifiutoPatta(
  risposta: unknown,
  mappaDominio: Readonly<Record<string, string>>,
  ripiego: string,
): string {
  const codice = codiceErrore(risposta);
  if (!codice) return ripiego;
  return CHIAVI_RIFIUTO_PATTA[codice] ?? mappaDominio[codice] ?? ripiego;
}

/**
 * Le due chiavi da mettere sul bottone Patta quando il freno lo spegne, o
 * `null` se il bottone va lasciato acceso.
 *
 * Ritorna CHIAVI e non frasi: chi la chiama ha il suo `t`, e una funzione che
 * traduce non si potrebbe provare senza montare i18n.
 *
 * L'ordine e' quello del server (`room.offerDraw`): prima il tetto, poi il
 * blocco mossa. A quota esaurita «muovi e riprova» sarebbe una bugia — muovere
 * non riaprirebbe niente — ed e' esattamente il genere di messaggio che manda a
 * riprovare chi non ha piu' niente da riprovare.
 */
export function etichettePattaBloccata(
  drawOffersLeft: number,
  drawMustMove: boolean,
): { label: string; reason: string } | null {
  if (drawOffersLeft <= 0) {
    return { label: 'game.actions.drawNoneLeft', reason: 'game.actions.drawLimitReached' };
  }
  if (drawMustMove) {
    return { label: 'game.actions.drawMoveFirst', reason: 'game.actions.drawMustMoveFirst' };
  }
  return null;
}
